import type { ObjectDirective } from 'vue'

export interface DialogContainerOptions {
  close?: () => void
  returnFocus?: HTMLElement | null | (() => HTMLElement | null | undefined)
  busy?: boolean
  initial?: string
  body?: string
  title?: string
}
const selector = 'button, a[href], input, select, textarea, [tabindex]'
const states = new WeakMap<HTMLElement, ReturnType<typeof createContainer>>()
const stack: HTMLElement[] = []
const inertScopes = new WeakMap<HTMLElement, { count: number; original: string | null }>()
let nextId = 0

/** Preserve the existing non-native modal contract, including overlapping scopes. */
function makeBackgroundInert(dialog: HTMLElement) {
  const affected: HTMLElement[] = []
  for (let branch: HTMLElement | null = dialog; branch && branch !== document.body; branch = branch.parentElement) {
    for (const sibling of branch.parentElement?.children ?? []) {
      if (!(sibling instanceof HTMLElement) || sibling === branch) continue
      const lease = inertScopes.get(sibling) ?? { count: 0, original: sibling.getAttribute('inert') }
      lease.count++; inertScopes.set(sibling, lease)
      sibling.setAttribute('inert', ''); affected.push(sibling)
    }
  }
  return () => {
    for (const element of affected) {
      const lease = inertScopes.get(element)!
      if (--lease.count > 0) continue
      if (lease.original === null) element.removeAttribute('inert')
      else element.setAttribute('inert', lease.original)
      inertScopes.delete(element)
    }
  }
}

export function dialogFocusables(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(selector)].filter(element => {
    if (element.matches(':disabled, [tabindex="-1"], input[type="hidden"]') || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false
    for (let node: HTMLElement | null = element; node && node !== container; node = node.parentElement) {
      const style = getComputedStyle(node)
      if (style.display === 'none' || style.visibility === 'hidden') return false
    }
    return true
  })
}

function createContainer(element: HTMLElement, initialOptions: DialogContainerOptions) {
  let options = initialOptions
  let opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
  let active = false
  let restoreBackground: (() => void) | undefined
  const native = element instanceof HTMLDialogElement
  const originalShow = native ? element.showModal : undefined
  const originalClose = native ? element.close : undefined
  element.classList.add('fw-dialog-container')
  if (!element.hasAttribute('role')) element.setAttribute('role', 'dialog')
  element.setAttribute('aria-modal', 'true')
  if (!element.hasAttribute('tabindex')) element.tabIndex = -1

  function structure() {
    let title = options.title ? element.querySelector<HTMLElement>(options.title)
      : element.querySelector<HTMLElement>('h1, h2, h3, [data-dialog-title]') ?? element.querySelector<HTMLElement>('.title, .ops-dialog-title, .drawer-title')
    if (!title && element.getAttribute('aria-label')) {
      title = document.createElement('h2')
      title.className = 'fw-dialog-accessible-title'
      // Headerless design popovers retain their localized name through a referenced heading.
      title.hidden = true
      title.textContent = element.getAttribute('aria-label')
      element.prepend(title)
    }
    if (title) {
      title.id ||= `fw-dialog-title-${++nextId}`
      element.setAttribute('aria-labelledby', title.id)
      if (title.classList.contains('fw-dialog-accessible-title')) title.textContent = element.getAttribute('aria-label')
    }
    const body = element.querySelector<HTMLElement>(options.body ?? '[data-dialog-body], .feature-dialog-body, .dialog-body, .drawer-body, .body, .console-search-results')
    if (body) {
      body.classList.add('fw-dialog-body')
      for (let parent = body.parentElement; parent && parent !== element; parent = parent.parentElement) parent.classList.add('fw-dialog-layout')
    }
  }
  function initial() {
    const items = dialogFocusables(element)
    const requested = options.initial ? element.querySelector<HTMLElement>(options.initial) : null
    const safe = element.querySelector<HTMLElement>('[data-dialog-cancel]')
    return requested && items.includes(requested) ? requested
      : element.getAttribute('role') === 'alertdialog' ? safe ?? items.find(item => item.matches('button')) ?? element
        : items.find(item => item.matches('input, select, textarea, [role="combobox"]') && !item.matches('[readonly], [aria-disabled="true"]')) ?? items[0] ?? element
  }
  function activate() {
    if (active) return
    active = true
    element.setAttribute('data-escape-layer', '')
    stack.push(element)
    structure()
    if (!native) restoreBackground = makeBackgroundInert(element)
    initial().focus({ preventScroll: true })
    document.addEventListener('focusin', contain)
  }
  function release() {
    if (!active) return
    active = false
    element.removeAttribute('data-escape-layer')
    const top = stack.at(-1) === element
    const index = stack.lastIndexOf(element)
    if (index >= 0) stack.splice(index, 1)
    document.removeEventListener('focusin', contain)
    restoreBackground?.(); restoreBackground = undefined
    const returnTarget = typeof options.returnFocus === 'function' ? options.returnFocus() : options.returnFocus
    const target = returnTarget ?? opener
    if (top && target?.isConnected) target.focus({ preventScroll: true })
  }
  function contain(event: FocusEvent) {
    if (stack.at(-1) === element && event.target instanceof Node && !element.contains(event.target)) initial().focus({ preventScroll: true })
  }
  function keydown(event: KeyboardEvent) {
    if (!active || stack.at(-1) !== element) return
    if (event.key === 'Escape') {
      if (event.defaultPrevented) return
      const layer = event.target instanceof Element
        ? event.target.closest('[data-escape-layer], [role="menu"], [role="listbox"], [popover], [role="dialog"], [role="alertdialog"]') : null
      // An inner layer owns this keystroke locally, even before its DOM is removed.
      if (layer && layer !== element && element.contains(layer)) return
      // Focus may remain on an empty picker's trigger or move to another field.
      if (element.querySelector('[data-escape-layer]')) return
      event.preventDefault(); event.stopPropagation()
      if (!options.busy && element.getAttribute('aria-busy') !== 'true') {
        if (options.close) options.close()
        else if (native) element.close()
      }
    }
    if (event.key !== 'Tab') return
    const items = dialogFocusables(element), first = items[0], last = items.at(-1)
    if (!first || !last) { event.preventDefault(); element.focus(); return }
    const index = items.indexOf(document.activeElement as HTMLElement)
    if (event.shiftKey && index <= 0) { event.preventDefault(); last.focus() }
    else if (!event.shiftKey && (index < 0 || index === items.length - 1)) { event.preventDefault(); first.focus() }
  }
  structure()
  element.addEventListener('keydown', keydown)
  element.addEventListener('close', release)
  if (native && originalShow && originalClose) {
    element.showModal = () => {
      opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
      originalShow.call(element)
      activate()
    }
    element.close = (value?: string) => { originalClose.call(element, value); release() }
    if (element.open) activate()
  } else activate()
  return {
    update(value: DialogContainerOptions) { options = value; structure() },
    destroy() {
      release()
      element.removeEventListener('keydown', keydown)
      element.removeEventListener('close', release)
      if (native && originalShow && originalClose) { element.showModal = originalShow; element.close = originalClose }
    },
  }
}

/** Opt-in container for the existing Cosmos dialog/drawer markup; never installed in Work. */
export const vDialogContainer: ObjectDirective<HTMLElement, DialogContainerOptions | undefined> = {
  mounted(element, { value }) { states.set(element, createContainer(element, value ?? {})) },
  updated(element, { value }) { states.get(element)?.update(value ?? {}) },
  beforeUnmount(element) { states.get(element)?.destroy(); states.delete(element) },
}
